import { zodResolver } from '@hookform/resolvers/zod';
import { IonContent } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Lock, Mail } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import {
  AUTH_FIELD_LABEL_CLASS,
  AuthLayoutWrapper,
} from '@/core/components/auth/auth-layout-wrapper';
import { useAuthFlow } from '@/core/hooks/use-auth-flow';
import { useAuthPublicConfig } from '@/core/hooks/use-auth-public-config';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { AppIonPage } from '@/core/components/app-ion-page';

export function LoginPage() {
  const router = useIonRouter();
  const location = useLocation();
  const dict = useDictionary();
  const {
    resolveLogin,
    sendLoginCode,
    loginWithPassword,
    commonDict,
    authRegisterDict,
  } = useAuthFlow();
  const { registrationEnabled } = useAuthPublicConfig();
  const invalidEmailError = useMemo(
    () => dict.auth.errors.invalidEmail,
    [dict.auth.errors.invalidEmail],
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const accountFromParams = searchParams.get('account')?.trim() || '';
  const magicCodeFromParams = searchParams.get('magicCode')?.trim() || '';

  const emailSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(invalidEmailError),
      }),
    [invalidEmailError],
  );

  const passwordSchema = useMemo(
    () =>
      z.object({
        password: z.string().min(8, dict.auth.errors.passwordTooShort),
      }),
    [dict.auth.errors.passwordTooShort],
  );

  type EmailFormData = z.infer<typeof emailSchema>;
  type PasswordFormData = z.infer<typeof passwordSchema>;

  const emailDefaults = useMemo(
    () => ({ email: accountFromParams }),
    [accountFromParams],
  );
  const passwordDefaults = useMemo(() => ({ password: '' }), []);

  const emailForm = useForm<EmailFormData>({
    mode: 'onTouched',
    resolver: zodResolver(emailSchema as never),
    defaultValues: emailDefaults,
  });

  const passwordForm = useForm<PasswordFormData>({
    mode: 'onTouched',
    resolver: zodResolver(passwordSchema as never),
    defaultValues: passwordDefaults,
  });

  const emailValue = useWatch({ control: emailForm.control, name: 'email' });
  const passwordValue = useWatch({
    control: passwordForm.control,
    name: 'password',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [resolvedEmail, setResolvedEmail] = useState('');

  // Backend / Maildev “open entry page” uses
  // `/auth/login?account=…&magicCode=…` — hand off to VerifyPage auto-submit.
  const magicLinkKey = `${accountFromParams}|${magicCodeFromParams}`;
  const handledMagicLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!accountFromParams || magicCodeFromParams.length !== 6) return;
    if (handledMagicLinkRef.current === magicLinkKey) return;
    handledMagicLinkRef.current = magicLinkKey;
    const params = new URLSearchParams({
      email: accountFromParams,
      code: magicCodeFromParams,
    });
    router.push(`/auth/verify?${params.toString()}`, 'forward', 'replace');
  }, [accountFromParams, magicCodeFromParams, magicLinkKey, router]);

  // Prefill when only `account` is present (e.g. resend / mid-flow).
  useEffect(() => {
    if (!accountFromParams || magicCodeFromParams.length === 6) return;
    emailForm.reset({ email: accountFromParams });
  }, [accountFromParams, magicCodeFromParams, emailForm]);

  const goToVerify = (email: string) => {
    router.push(
      `/auth/verify?email=${encodeURIComponent(email)}`,
      'forward',
      'push',
    );
  };

  const handleRequiresRegistration = (email: string) => {
    if (registrationEnabled === false) {
      setError(authRegisterDict.registrationsDisabled);
      return false;
    }
    router.push(
      `/auth/register?email=${encodeURIComponent(email)}`,
      'forward',
      'push',
    );
    return true;
  };

  const onEmailContinue = emailForm.handleSubmit(async (values) => {
    setIsLoading(true);
    setError(null);

    const resolved = await resolveLogin(values.email);
    if (!resolved.success) {
      if (resolved.requiresRegistration) {
        const navigated = handleRequiresRegistration(values.email);
        setIsLoading(false);
        if (!navigated) return;
        return;
      }
      setError(resolved.error || dict.auth.errors.requestFailed);
      setIsLoading(false);
      return;
    }

    if (resolved.hasPassword) {
      setResolvedEmail(values.email);
      setStep('password');
      passwordForm.reset({ password: '' });
      setIsLoading(false);
      return;
    }

    const result = await sendLoginCode(values.email);
    if (result.success) {
      setIsLoading(false);
      goToVerify(values.email);
      return;
    }
    if (result.requiresRegistration) {
      const navigated = handleRequiresRegistration(values.email);
      setIsLoading(false);
      if (!navigated) return;
      return;
    }
    setError(result.error || dict.auth.errors.requestFailed);
    setIsLoading(false);
  });

  const onPasswordSubmit = passwordForm.handleSubmit(async (values) => {
    setIsLoading(true);
    setError(null);
    const result = await loginWithPassword(resolvedEmail, values.password);
    if (!result.success) {
      setError(result.error || dict.auth.errors.invalidPassword);
      setIsLoading(false);
      return;
    }
    if (result.requiresCompletion) {
      setIsLoading(false);
      router.push('/auth/complete', 'forward', 'push');
      return;
    }
    setIsLoading(false);
  });

  const onUseOtpInstead = async () => {
    setIsLoading(true);
    setError(null);
    const result = await sendLoginCode(resolvedEmail);
    if (result.success) {
      setIsLoading(false);
      goToVerify(resolvedEmail);
      return;
    }
    if (result.requiresRegistration) {
      const navigated = handleRequiresRegistration(resolvedEmail);
      setIsLoading(false);
      if (!navigated) return;
      return;
    }
    setError(result.error || dict.auth.errors.requestFailed);
    setIsLoading(false);
  };

  return (
    <AppIonPage>
      <IonContent className="ion-padding" fullscreen scrollY={false}>
        <div className="bg-background text-foreground flex h-full w-full safe-area-padding">
          <AuthLayoutWrapper
            title={commonDict.title}
            description={commonDict.description}
            backTo="/auth/welcome"
          >
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === 'email' ? (
              <form onSubmit={onEmailContinue} className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className={AUTH_FIELD_LABEL_CLASS}>
                    {commonDict.emailLabel}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Controller
                      control={emailForm.control}
                      name="email"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          id="email"
                          type="email"
                          autoComplete="email"
                          enterKeyHint="go"
                          confirmOnEnter={false}
                          placeholder={commonDict.emailPlaceholder}
                          className="pl-10"
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                        />
                      )}
                    />
                  </div>
                  {emailForm.formState.errors.email &&
                    emailValue &&
                    emailValue.length > 0 && (
                      <p className="text-[13.5px] text-destructive">
                        {emailForm.formState.errors.email.message}
                      </p>
                    )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  radius="full"
                  className="w-full"
                  disabled={!emailValue || emailValue.length === 0}
                  isLoading={isLoading}
                >
                  {commonDict.continue}
                </Button>
              </form>
            ) : (
              <form onSubmit={onPasswordSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="password-email"
                    className={AUTH_FIELD_LABEL_CLASS}
                  >
                    {commonDict.emailLabel}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password-email"
                      type="email"
                      autoComplete="username"
                      className="pl-10"
                      value={resolvedEmail}
                      readOnly
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="password" className={AUTH_FIELD_LABEL_CLASS}>
                    {dict.auth.login.passwordLabel}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Controller
                      control={passwordForm.control}
                      name="password"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          id="password"
                          type="password"
                          autoComplete="current-password"
                          enterKeyHint="go"
                          confirmOnEnter={false}
                          placeholder={dict.auth.login.passwordPlaceholder}
                          className="pl-10"
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                        />
                      )}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  radius="full"
                  className="w-full"
                  disabled={!passwordValue || passwordValue.length < 8}
                  isLoading={isLoading}
                >
                  {dict.auth.login.passwordSubmit}
                </Button>

                <button
                  type="button"
                  className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50"
                  disabled={isLoading}
                  onClick={() => {
                    void onUseOtpInstead();
                  }}
                >
                  {dict.auth.login.useOtpInstead}
                </button>

                <button
                  type="button"
                  className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50"
                  disabled={isLoading}
                  onClick={() => {
                    setStep('email');
                    setError(null);
                    passwordForm.reset({ password: '' });
                  }}
                >
                  {dict.auth.login.changeEmail}
                </button>
              </form>
            )}
          </AuthLayoutWrapper>
        </div>
      </IonContent>
    </AppIonPage>
  );
}
